import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Scan, MapPin, Package, Check, ArrowRight, Edit, CheckCircle, FileText, Camera } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';
import BarcodeScanner from './BarcodeScanner';

interface CountItem {
  id: string;
  product_id: string;
  location: string;
  lot: string | null;
  expected_quantity: number;
  actual_quantity: number | null;
  status: 'pending' | 'in_progress' | 'completed';
  csv_line_number: number | null;
  original_worksheet_id: string | null;
  products: {
    item_ref: string;
    name: string;
    unit: string;
  };
}

interface LocationGroup {
  location: string;
  items: CountItem[];
  completed: boolean;
}

type ScannerMode = 'location' | 'location_contents' | 'location_edit' | 'product' | 'quantity' | 'no_barcode' | 'location_review' | 'session_complete' | 'confirm_variance' | 'edit_location' | 'edit_add_item' | 'edit_quantity' | 'edit_no_barcode' | 'confirm_missing_items';

const CountSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [session, setSession] = useState<any>(null);
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [mode, setMode] = useState<ScannerMode>('location');
  const [loading, setLoading] = useState(true);
  const [editingLocationIndex, setEditingLocationIndex] = useState<number | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [barcode, setBarcode] = useState('');
  const [scanError, setScanError] = useState<string>('');
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState<'location' | 'product'>('location');

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
      fetchAllProducts();
    }
  }, [sessionId]);

  // Check if session is already complete on load
  useEffect(() => {
    if (session && locations.length > 0) {
      // If the session is marked as completed or reopened, go to complete screen
      if (session.status === 'completed') {
        handleSessionReopen();
        setMode('session_complete');
      } else if (session.status === 'reopened') {
        setMode('session_complete');
      } else {
        // Otherwise, check if all locations are actually completed
        const allLocationsComplete = locations.every(loc => loc.completed);
        if (allLocationsComplete) {
          // Show the review screen but don't mark as completed automatically
          setMode('session_complete');
        }
      }
    }
  }, [session, locations]);

  const handleSessionReopen = async () => {
    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ status: 'reopened' })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating session status to reopened:', error);
      } else {
        // Update local session state
        setSession((prev: any) => prev ? { ...prev, status: 'reopened' } : null);
      }
    } catch (error) {
      console.error('Error in handleSessionReopen:', error);
    }
  };

  const fetchSessionData = async () => {
    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('count_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      setSession(sessionData);

      const { data: itemsData, error: itemsError } = await supabase
        .from('count_items')
        .select(`
          *,
          products (
            item_ref,
            name,
            unit
          )
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (itemsError) throw itemsError;
      
      // Group items by location
      const locationGroups: LocationGroup[] = [];
      const locationMap = new Map<string, CountItem[]>();
      
      itemsData.forEach(item => {
        if (!locationMap.has(item.location)) {
          locationMap.set(item.location, []);
        }
        locationMap.get(item.location)!.push(item);
      });

      locationMap.forEach((items, location) => {
        locationGroups.push({
          location,
          items,
          completed: items.every(item => item.status === 'completed')
        });
      });

      setLocations(locationGroups);
      
      // Find where to resume - go to the first incomplete location
      let resumeIndex = 0;
      
      // Find the first location that is not completed
      const firstIncompleteIndex = locationGroups.findIndex(loc => !loc.completed);
      
      if (firstIncompleteIndex !== -1) {
        // Found an incomplete location, resume there
        resumeIndex = firstIncompleteIndex;
      } else {
        // All locations are complete, go to session complete screen
        resumeIndex = 0; // Will be handled by the session complete logic in useEffect
      }
      
      setCurrentLocationIndex(resumeIndex);
      
    } catch (error) {
      console.error('Error fetching session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          item_ref,
          name,
          unit
        `)
        .order('name', { ascending: true });

      if (error) throw error;
      setAllProducts(data || []);
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
  };

  const handleLocationScan = (barcode: string) => {
    const expectedLocation = currentLocation.location;
    
    // Clear any previous error
    setLocationError('');
    
    // Validate that the scanned barcode matches the expected location
    if (barcode.trim().toUpperCase() === expectedLocation.toUpperCase()) {
      setScannedBarcode(barcode);
      setMode('location_contents');
      setTimeout(() => setScannedBarcode(''), 3000);
    } else {
      // Show error for wrong location
      setLocationError(`Wrong location! Expected: ${expectedLocation}, but you scanned: ${barcode}`);
      setScannedBarcode('');
      // Clear error after 5 seconds
      setTimeout(() => setLocationError(''), 5000);
    }
  };

  // Camera barcode scanner handlers
  const handleCameraScan = (scannedCode: string) => {
    setShowBarcodeScanner(false);
    
    if (scannerMode === 'location') {
      handleLocationScan(scannedCode);
    } else if (scannerMode === 'product') {
      handleProductScan(scannedCode);
    }
  };

  const openCameraScanner = (mode: 'location' | 'product') => {
    setScannerMode(mode);
    setShowBarcodeScanner(true);
  };

  const handleEditBarcodeSubmit = async () => {
    if (!barcode.trim() || editingLocationIndex === null) return;

    setScanError('');
    
    try {
      const { data: barcodeData, error } = await supabase
        .from('barcodes')
        .select('product_id')
        .eq('barcode', barcode)
        .single();

      if (error || !barcodeData) {
        setScanError('Product not found. Try scanning again or select from list.');
        setTimeout(() => setScanError(''), 5000);
        return;
      }

      const editingLocation = locations[editingLocationIndex];
      
      // Check if product is already in this location
      const itemIndex = editingLocation.items.findIndex(
        item => item.product_id === barcodeData.product_id
      );

      if (itemIndex !== -1) {
        // Product exists in this location, edit it
        setCurrentItemIndex(itemIndex);
        setMode('edit_quantity');
        setBarcode('');
        return;
      }

      // Product not in this location, add it as new item
      try {
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', barcodeData.product_id)
          .single();

        if (productError) throw productError;

        // Get the original worksheet ID from existing items in the session
        const originalWorksheetId = editingLocation.items.length > 0 
          ? editingLocation.items[0].original_worksheet_id 
          : null;
        
        // Get the highest csv_line_number to append new items at the end
        const allEditSessionItems = locations.flatMap(loc => loc.items);
        const maxEditLineNumber = Math.max(...allEditSessionItems.map(item => item.csv_line_number || 0));
        const newEditCsvLineNumber = maxEditLineNumber + 1000;

        const { data: newItem, error: insertError } = await supabase
          .from('count_items')
          .insert({
            session_id: sessionId,
            product_id: barcodeData.product_id,
            location: editingLocation.location,
            expected_quantity: 0, // Unexpected find
            status: 'pending',
            csv_line_number: newEditCsvLineNumber,
            original_worksheet_id: originalWorksheetId
          })
          .select(`
            *,
            products (
              item_ref,
              name,
              unit
            )
          `)
          .single();

        if (insertError) throw insertError;

        // Add to editing location
        const updatedLocations = [...locations];
        updatedLocations[editingLocationIndex].items.push(newItem);
        setLocations(updatedLocations);
        
        // Set as current item
        setCurrentItemIndex(updatedLocations[editingLocationIndex].items.length - 1);
        setMode('edit_quantity');
        setBarcode('');
        
      } catch (error) {
        console.error('Error adding new product:', error);
        setScanError('Error adding product to this location');
        setTimeout(() => setScanError(''), 5000);
      }
      
    } catch (error) {
      console.error('Error verifying barcode:', error);
      setScanError('Error verifying barcode');
      setTimeout(() => setScanError(''), 5000);
    }
  };

  const handleProductScan = async (barcode: string) => {
    if (!barcode.trim()) return;

    // Clear any previous error
    setScanError('');

    try {
      const currentLocation = locations[currentLocationIndex];
      
      const { data: barcodeData, error } = await supabase
        .from('barcodes')
        .select('product_id')
        .eq('barcode', barcode)
        .single();

      if (error || !barcodeData) {
        setScanError('Barcode did not match any item');
        setTimeout(() => setScanError(''), 5000);
        return;
      }

      // First, try to find the product in expected items
      const itemIndex = currentLocation.items.findIndex(
        item => item.product_id === barcodeData.product_id && item.status !== 'completed'
      );

      if (itemIndex !== -1) {
        // Product found in expected items - proceed normally
        setCurrentItemIndex(itemIndex);
        setScannedBarcode(barcode);
        setMode('quantity');
        setTimeout(() => setScannedBarcode(''), 3000);
        return;
      }

      // Product not expected - check if it's already been counted as an unexpected item
      const completedItemIndex = currentLocation.items.findIndex(
        item => item.product_id === barcodeData.product_id && item.status === 'completed'
      );

      if (completedItemIndex !== -1) {
        alert('This product has already been counted in this location.');
        return;
      }

      // Product not expected and not yet counted - add it as an unexpected find
      if (!user) return;
      
      // Get product details
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', barcodeData.product_id)
        .single();

      if (productError || !productData) {
        alert('Error getting product details');
        return;
      }

      // Get the original worksheet ID from existing items in the session
      const originalWorksheetId = currentLocation.items.length > 0 
        ? currentLocation.items[0].original_worksheet_id 
        : null;
      
      // Get the highest csv_line_number to append new items at the end
      const allSessionItems = locations.flatMap(loc => loc.items);
      const maxLineNumber = Math.max(...allSessionItems.map(item => item.csv_line_number || 0));
      const newCsvLineNumber = maxLineNumber + 1000; // Add large number to put at end

      const { data: newItem, error: insertError } = await supabase
        .from('count_items')
        .insert({
          session_id: sessionId,
          product_id: barcodeData.product_id,
          location: currentLocation.location,
          expected_quantity: 0, // Unexpected find
          status: 'pending',
          csv_line_number: newCsvLineNumber,
          original_worksheet_id: originalWorksheetId
        })
        .select(`
          *,
          products (
            item_ref,
            name,
            unit
          )
        `)
        .single();

      if (insertError) throw insertError;

      // Add to current location
      const updatedLocations = [...locations];
      updatedLocations[currentLocationIndex].items.push(newItem);
      setLocations(updatedLocations);
      
      // Set as current item
      setCurrentItemIndex(updatedLocations[currentLocationIndex].items.length - 1);
      setScannedBarcode(barcode);
      setMode('quantity');
      setTimeout(() => setScannedBarcode(''), 3000);
      
    } catch (error) {
      console.error('Error verifying barcode:', error);
      setScanError('Error verifying barcode');
      setTimeout(() => setScanError(''), 5000);
    }
  };

  const handleProductSelect = async (itemIndex: number, isFromCurrentLocation: boolean = true) => {
    if (isFromCurrentLocation) {
      setCurrentItemIndex(itemIndex);
      setMode('quantity');
    } else {
      // Product found in unexpected location - create new count item
      if (!user) return;
      
      try {
        const allItems = locations.flatMap(loc => loc.items);
        const uniqueProducts = allItems.filter((item, index, arr) => 
          arr.findIndex(i => i.product_id === item.product_id) === index
        );
        const selectedItem = uniqueProducts[itemIndex];
        
        // Get the original worksheet ID from existing items in the session
        const originalWorksheetId = currentLocation.items.length > 0 
          ? currentLocation.items[0].original_worksheet_id 
          : null;
        
        // Get the highest csv_line_number to append new items at the end
        const allSessionItems = locations.flatMap(loc => loc.items);
        const maxLineNumber = Math.max(...allSessionItems.map(item => item.csv_line_number || 0));
        const newCsvLineNumber = maxLineNumber + 1000; // Add large number to put at end

        const { data: newItem, error } = await supabase
          .from('count_items')
          .insert({
            session_id: sessionId,
            product_id: selectedItem.product_id,
            location: currentLocation.location,
            expected_quantity: 0, // Unexpected find
            status: 'pending',
            csv_line_number: newCsvLineNumber,
            original_worksheet_id: originalWorksheetId
          })
          .select(`
            *,
            products (
              item_ref,
              name,
              unit
            )
          `)
          .single();

        if (error) throw error;

        // Add to current location
        const updatedLocations = [...locations];
        updatedLocations[currentLocationIndex].items.push(newItem);
        setLocations(updatedLocations);
        
        // Set as current item
        setCurrentItemIndex(updatedLocations[currentLocationIndex].items.length - 1);
        setMode('quantity');
        
      } catch (error) {
        console.error('Error creating new count item:', error);
        alert('Error adding product to this location');
      }
    }
  };

  const handleProductSelectFromMaster = async (product: any) => {
    // This handles selecting a product from the master product list (not in current session)
    if (!user) return;
    
    try {
      const currentLocation = locations[currentLocationIndex];
      
      // Get the original worksheet ID from existing items in the session
      const originalWorksheetId = currentLocation.items.length > 0 
        ? currentLocation.items[0].original_worksheet_id 
        : null;
      
      // Get the highest csv_line_number to append new items at the end
      const allSessionItems = locations.flatMap(loc => loc.items);
      const maxLineNumber = Math.max(...allSessionItems.map(item => item.csv_line_number || 0));
      const newCsvLineNumber = maxLineNumber + 1000; // Add large number to put at end

      const { data: newItem, error } = await supabase
        .from('count_items')
        .insert({
          session_id: sessionId,
          product_id: product.id,
          location: currentLocation.location,
          expected_quantity: 0, // Unexpected find
          status: 'pending',
          csv_line_number: newCsvLineNumber,
          original_worksheet_id: originalWorksheetId
        })
        .select(`
          *,
          products (
            item_ref,
            name,
            unit
          )
        `)
        .single();

      if (error) throw error;

      // Add to current location
      const updatedLocations = [...locations];
      updatedLocations[currentLocationIndex].items.push(newItem);
      setLocations(updatedLocations);
      
      // Set as current item
      setCurrentItemIndex(updatedLocations[currentLocationIndex].items.length - 1);
      setMode('quantity');
      
    } catch (error) {
      console.error('Error creating new count item:', error);
      alert('Error adding product to this location');
    }
  };

  const handleEditProductSelect = async (itemIndex: number, isFromCurrentLocation: boolean = true) => {
    if (editingLocationIndex === null) return;
    
    if (isFromCurrentLocation) {
      setCurrentItemIndex(itemIndex);
      setMode('edit_quantity');
    } else {
      // Product found in unexpected location - create new count item
      if (!user) return;
      
      try {
        const allItems = locations.flatMap(loc => loc.items);
        const uniqueProducts = allItems.filter((item, index, arr) => 
          arr.findIndex(i => i.product_id === item.product_id) === index
        );
        const selectedItem = uniqueProducts[itemIndex];
        
        // Get the original worksheet ID from existing items in the session
        const editingLocation = locations[editingLocationIndex];
        const originalWorksheetId = editingLocation.items.length > 0 
          ? editingLocation.items[0].original_worksheet_id 
          : null;
        
        // Get the highest csv_line_number to append new items at the end
        const allEditSessionItems = locations.flatMap(loc => loc.items);
        const maxEditLineNumber = Math.max(...allEditSessionItems.map(item => item.csv_line_number || 0));
        const newEditCsvLineNumber = maxEditLineNumber + 1000; // Add large number to put at end

        const { data: newItem, error } = await supabase
          .from('count_items')
          .insert({
            session_id: sessionId,
            product_id: selectedItem.product_id,
            location: locations[editingLocationIndex].location,
            expected_quantity: 0, // Unexpected find
            status: 'pending',
            csv_line_number: newEditCsvLineNumber,
            original_worksheet_id: originalWorksheetId
          })
          .select(`
            *,
            products (
              item_ref,
              name,
              unit
            )
          `)
          .single();

        if (error) throw error;

        // Add to editing location
        const updatedLocations = [...locations];
        updatedLocations[editingLocationIndex].items.push(newItem);
        setLocations(updatedLocations);
        
        // Set as current item
        setCurrentItemIndex(updatedLocations[editingLocationIndex].items.length - 1);
        setMode('edit_quantity');
        
      } catch (error) {
        console.error('Error creating new count item:', error);
        alert('Error adding product to this location');
      }
    }
  };

  const handleEditProductSelectFromMaster = async (product: any) => {
    // This handles selecting a product from the master product list for edit mode
    if (editingLocationIndex === null || !user) return;
    
    try {
      // Get the original worksheet ID from existing items in the session
      const editingLocation = locations[editingLocationIndex];
      const originalWorksheetId = editingLocation.items.length > 0 
        ? editingLocation.items[0].original_worksheet_id 
        : null;
      
      // Get the highest csv_line_number to append new items at the end
      const allEditSessionItems = locations.flatMap(loc => loc.items);
      const maxEditLineNumber = Math.max(...allEditSessionItems.map(item => item.csv_line_number || 0));
      const newEditCsvLineNumber = maxEditLineNumber + 1000;

      const { data: newItem, error } = await supabase
        .from('count_items')
        .insert({
          session_id: sessionId,
          product_id: product.id,
          location: locations[editingLocationIndex].location,
          expected_quantity: 0, // Unexpected find
          status: 'pending',
          csv_line_number: newEditCsvLineNumber,
          original_worksheet_id: originalWorksheetId
        })
        .select(`
          *,
          products (
            item_ref,
            name,
            unit
          )
        `)
        .single();

      if (error) throw error;

      // Add to editing location
      const updatedLocations = [...locations];
      updatedLocations[editingLocationIndex].items.push(newItem);
      setLocations(updatedLocations);
      
      // Set as current item
      setCurrentItemIndex(updatedLocations[editingLocationIndex].items.length - 1);
      setMode('edit_quantity');
      
    } catch (error) {
      console.error('Error creating new count item:', error);
      alert('Error adding product to this location');
    }
  };

  const handleEditQuantitySubmit = async () => {
    if (!quantity || !user || editingLocationIndex === null) return;

    try {
      const editingLocation = locations[editingLocationIndex];
      const currentItem = editingLocation.items[currentItemIndex];
      const actualQuantity = parseInt(quantity);

      const { error } = await supabase
        .from('count_items')
        .update({
          actual_quantity: actualQuantity,
          status: 'completed',
          counted_by: user.id,
          counted_at: new Date().toISOString()
        })
        .eq('id', currentItem.id);

      if (error) throw error;

      // Update local state
      const updatedLocations = [...locations];
      updatedLocations[editingLocationIndex].items[currentItemIndex] = {
        ...currentItem,
        actual_quantity: actualQuantity,
        status: 'completed'
      };
      setLocations(updatedLocations);

      setQuantity('');
      setBarcode('');
      setMode('edit_location');
      
    } catch (error) {
      console.error('Error submitting count:', error);
      alert('Error submitting count');
    }
  };

  const handleQuantitySubmit = async () => {
    if (!quantity || !user) return;

    const currentLocation = locations[currentLocationIndex];
    const currentItem = currentLocation.items[currentItemIndex];
    const actualQuantity = parseInt(quantity);

    // Check for significant variance (>30%) if expected quantity is not 0
    if (currentItem.expected_quantity > 0) {
      const variance = Math.abs(actualQuantity - currentItem.expected_quantity) / currentItem.expected_quantity * 100;
      
      if (variance > 30) {
        setMode('confirm_variance');
        return;
      }
    }

    await submitQuantity();
  };

  const submitQuantity = async () => {
    if (!quantity || !user) return;

    try {
      const currentLocation = locations[currentLocationIndex];
      const currentItem = currentLocation.items[currentItemIndex];
      const actualQuantity = parseInt(quantity);

      const { error } = await supabase
        .from('count_items')
        .update({
          actual_quantity: actualQuantity,
          status: 'completed',
          counted_by: user.id,
          counted_at: new Date().toISOString()
        })
        .eq('id', currentItem.id);

      if (error) throw error;

      // Update local state
      const updatedLocations = [...locations];
      updatedLocations[currentLocationIndex].items[currentItemIndex] = {
        ...currentItem,
        actual_quantity: actualQuantity,
        status: 'completed'
      };
      setLocations(updatedLocations);

      setQuantity('');
      
      // Return to location contents to review and decide next action
      setMode('location_contents');
      
    } catch (error) {
      console.error('Error submitting count:', error);
      alert('Error submitting count');
    }
  };



  const markAllPendingItemsAsComplete = async (locationName: string) => {
    if (!user) return;

    try {
      // Mark all pending items in this location and session as completed with quantity 0
      const { error } = await supabase
        .from('count_items')
        .update({
          actual_quantity: 0,
          status: 'completed',
          counted_by: user.id,
          counted_at: new Date().toISOString()
        })
        .eq('session_id', sessionId)
        .eq('location', locationName)
        .eq('status', 'pending');

      if (error) throw error;

      // Update local state to reflect database changes
      const updatedLocations = [...locations];
      const locationIndex = updatedLocations.findIndex(loc => loc.location === locationName);
      
      if (locationIndex !== -1) {
        updatedLocations[locationIndex].items = updatedLocations[locationIndex].items.map(item => {
          if (item.status === 'pending') {
            return {
              ...item,
              actual_quantity: 0,
              status: 'completed'
            };
          }
          return item;
        });
        updatedLocations[locationIndex].completed = true;
        setLocations(updatedLocations);
      }
      
    } catch (error) {
      console.error('Error marking pending items as complete:', error);
      alert('Error completing location');
    }
  };

  const handleConfirmLocation = async () => {
    const currentLocation = locations[currentLocationIndex];
    
    // Find all uncounted items in this location
    const uncompletedItems = currentLocation.items.filter(item => item.status !== 'completed');
    
    // If there are uncounted items, show warning screen
    if (uncompletedItems.length > 0) {
      setMode('confirm_missing_items');
      return;
    }

    // Even if no uncounted items in local state, ensure all pending items in database are marked as completed
    await markAllPendingItemsAsComplete(currentLocation.location);

    // If no uncounted items, proceed directly to next location
    await proceedToNextLocation();
  };

  const handleConfirmMissingItems = async () => {
    if (!user) return;

    try {
      const currentLocation = locations[currentLocationIndex];
      
      // Find all uncounted items in this location
      const uncompletedItems = currentLocation.items.filter(item => item.status !== 'completed');
      
      // Mark all uncounted items as 0 quantity
      if (uncompletedItems.length > 0) {
        const itemIds = uncompletedItems.map(item => item.id);
        
        const { error } = await supabase
          .from('count_items')
          .update({
            actual_quantity: 0,
            status: 'completed',
            counted_by: user.id,
            counted_at: new Date().toISOString()
          })
          .in('id', itemIds);

        if (error) throw error;
      }

      // Update local state
      const updatedLocations = [...locations];
      updatedLocations[currentLocationIndex].items = updatedLocations[currentLocationIndex].items.map(item => {
        if (item.status !== 'completed') {
          return {
            ...item,
            actual_quantity: 0,
            status: 'completed'
          };
        }
        return item;
      });
      updatedLocations[currentLocationIndex].completed = true;
      setLocations(updatedLocations);

      await proceedToNextLocation();
      
    } catch (error) {
      console.error('Error completing location:', error);
      alert('Error completing location');
    }
  };

  const proceedToNextLocation = async () => {
    // Clear any location error when moving to next location
    setLocationError('');

    // Check if all locations are complete
    const nextLocationIndex = locations.findIndex((loc, index) => 
      index > currentLocationIndex && !loc.completed
    );
    
    if (nextLocationIndex !== -1) {
      setCurrentLocationIndex(nextLocationIndex);
      setMode('location');
    } else {
      setMode('session_complete');
    }
  };

  const handleEditLocation = (locationIndex: number) => {
    setEditingLocationIndex(locationIndex);
    setMode('edit_location');
  };

  const handleCloseSession = async () => {
    if (!sessionId) return;

    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ status: 'completed' })
        .eq('id', sessionId);

      if (error) throw error;

      // Update local session state
      setSession((prev: any) => prev ? { ...prev, status: 'completed' } : null);
      
      // Show success message
      alert('Count session closed successfully!');
      
      // Navigate back to dashboard
      navigate('/scanner');
      
    } catch (error) {
      console.error('Error closing session:', error);
      alert('Error closing session');
    }
  };

  const handleUpdateCount = async (itemId: string, newQuantity: number) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('count_items')
        .update({
          actual_quantity: newQuantity,
          status: 'completed',
          counted_by: user.id,
          counted_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;

      // Update local state
      const updatedLocations = [...locations];
      const locationIndex = editingLocationIndex!;
      const itemIndex = updatedLocations[locationIndex].items.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        updatedLocations[locationIndex].items[itemIndex] = {
          ...updatedLocations[locationIndex].items[itemIndex],
          actual_quantity: newQuantity,
          status: 'completed'
        };
        setLocations(updatedLocations);
      }
      
    } catch (error) {
      console.error('Error updating count:', error);
      alert('Error updating count');
    }
  };

  const handleUpdateCurrentLocationCount = async (itemId: string, newQuantity: number) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('count_items')
        .update({
          actual_quantity: newQuantity,
          status: 'completed',
          counted_by: user.id,
          counted_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;

      // Update local state for current location
      const updatedLocations = [...locations];
      const itemIndex = updatedLocations[currentLocationIndex].items.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        updatedLocations[currentLocationIndex].items[itemIndex] = {
          ...updatedLocations[currentLocationIndex].items[itemIndex],
          actual_quantity: newQuantity,
          status: 'completed'
        };
        setLocations(updatedLocations);
      }
      
    } catch (error) {
      console.error('Error updating count:', error);
      alert('Error updating count');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <div className="text-xl">Loading count session...</div>
        </div>
      </div>
    );
  }

  if (!session || locations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card bg-base-100 shadow-xl max-w-md">
          <div className="card-body text-center">
            <h2 className="card-title text-2xl mb-4">Session not found</h2>
            <p className="text-lg mb-6">
              The count session could not be found or has no items.
            </p>
            <button onClick={() => navigate('/scanner')} className="btn btn-primary btn-lg">
              Back to Scanner
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentLocation = locations[currentLocationIndex];
  const currentItem = currentLocation?.items[currentItemIndex];

  return (
    <div className="min-h-screen bg-base-200">
      <div className="w-full max-w-md mx-auto px-2 py-2">
        
        {/* Location Scan Mode */}
        {mode === 'location' && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-2">Go to Location</h1>
            
            <div className="text-4xl font-bold text-primary mb-3">
              {currentLocation.location}
            </div>
            
            {locationError && (
              <div className="alert alert-error mb-3 p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{locationError}</span>
              </div>
            )}

            <div className="w-full">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Scan location barcode"
                  className={`input input-bordered flex-1 text-center ${locationError ? 'input-error' : ''}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleLocationScan(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  onChange={() => setLocationError('')} // Clear error when user starts typing
                  autoFocus
                />
                <button
                  onClick={() => openCameraScanner('location')}
                  className="btn btn-secondary"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location Contents Mode */}
        {mode === 'location_contents' && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-3">Location {currentLocation.location}</h1>
              
              {/* Check if this is an empty location (only has EMPTY_LOCATION product) */}
              {currentLocation.items.length === 1 && currentLocation.items[0].products.item_ref === 'EMPTY_LOCATION' ? (
                <div className="mb-6">
                  <div className="alert alert-info mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>This location is expected to be empty - no products should be here.</span>
                  </div>
                  
                  {currentLocation.items[0].status === 'completed' && (
                    <div className="alert alert-success mb-6">
                      <Check className="w-6 h-6" />
                      <span>Location confirmed as empty!</span>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <button
                      className="btn btn-primary btn-lg w-full"
                      onClick={() => setMode('product')}
                    >
                      <Package className="w-6 h-6 mr-2" />
                      Found Items Here
                    </button>
                    
                    <button
                      className="btn btn-success btn-lg w-full"
                      onClick={() => setMode('location_review')}
                    >
                      <CheckCircle className="w-6 h-6 mr-2" />
                      Confirm Location is Empty
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  {/* Show counted items if any */}
                  {currentLocation.items.filter(item => item.status === 'completed').length > 0 && (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold">Already Counted:</h3>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => setMode('location_edit')}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                      </div>
                      <div className="space-y-2">
                        {currentLocation.items
                          .filter(item => item.status === 'completed')
                          .map((item) => (
                            <div key={item.id} className="flex justify-between items-center p-3 bg-base-200 rounded">
                              <div className="text-left">
                                <div className="font-medium">{item.products.name}</div>
                                <div className="text-sm opacity-70">{item.products.item_ref}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold">{item.actual_quantity}</div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <button
                      className="btn btn-primary btn-lg w-full"
                      onClick={() => setMode('product')}
                    >
                      <Package className="w-6 h-6 mr-2" />
                      Add More Items
                    </button>
                    
                    <button
                      className="btn btn-success btn-lg w-full"
                      onClick={() => setMode('location_review')}
                    >
                      <CheckCircle className="w-6 h-6 mr-2" />
                      No More Items Here
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Location Edit Mode */}
        {mode === 'location_edit' && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Edit Location</h1>

              </div>
              
              <div className="overflow-x-auto mb-6">
                <table className="table table-compact w-full">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Actual Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLocation.items
                      .filter(item => item.status === 'completed')
                      .map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div>
                              <div className="font-medium text-sm">{item.products.name}</div>
                              <div className="text-xs text-base-content/70">{item.products.item_ref}</div>
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-20 text-center"
                              value={item.actual_quantity || ''}
                              onChange={(e) => {
                                const newQuantity = parseInt(e.target.value) || 0;
                                handleUpdateCurrentLocationCount(item.id, newQuantity);
                              }}
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              <button
                className="btn btn-primary btn-lg w-full"
                onClick={() => setMode('location_contents')}
              >
                Done Editing
              </button>
            </div>
          </div>
        )}

        {/* Product Scan Mode */}
        {mode === 'product' && (
          <div className="text-center">
            <h1 className="text-lg font-bold mb-2">Scan Product In {currentLocation.location}</h1>
            
            <p className="text-sm mb-3">
              Scan the barcode of any product in this location
            </p>
            
            {scannedBarcode && (
              <div className="alert alert-success mb-2 p-2">
                <Check className="w-4 h-4" />
                <span className="text-sm">Product found!</span>
              </div>
            )}

            {scanError && (
              <div className="alert alert-error mb-2 p-2">
                <span className="text-sm">{scanError}</span>
              </div>
            )}

            <div className="w-full mb-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Scan product barcode"
                  className={`input input-bordered input-lg flex-1 text-center text-xl ${scanError ? 'input-error' : ''}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleProductScan(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  onChange={() => setScanError('')}
                  autoFocus
                />
                <button
                  onClick={() => openCameraScanner('product')}
                  className="btn btn-secondary btn-lg"
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <button
                className="btn btn-outline btn-lg w-full"
                onClick={() => setMode('no_barcode')}
              >
                No barcode
              </button>
              
              <button
                className="btn btn-warning btn-lg w-full"
                onClick={() => setMode('location_contents')}
              >
                Back to location
              </button>
            </div>
          </div>
        )}

        {/* No Barcode Mode */}
        {mode === 'no_barcode' && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Select Product</h1>
              
              <p className="text-lg mb-6">
                Select any product from the complete list:
              </p>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {/* Products expected in current location first */}
                <div className="text-sm font-semibold text-left mb-2">Expected in this location:</div>
                {currentLocation.items
                  .filter(item => item.status !== 'completed')
                  .map((item, index) => (
                    <button
                      key={`current-${item.id}`}
                      className="btn btn-outline btn-lg w-full text-left"
                      onClick={() => handleProductSelect(index, true)}
                    >
                      <div>
                        <div className="font-bold">{item.products.name}</div>
                        <div className="text-sm opacity-70">{item.products.item_ref}</div>
                      </div>
                    </button>
                  ))}
                
                {/* All other unique products */}
                <div className="text-sm font-semibold text-left mb-2 mt-4">All other products:</div>
                {(() => {
                  const currentLocationProductIds = currentLocation.items.map(item => item.product_id);
                  const otherProducts = allProducts.filter(product => 
                    !currentLocationProductIds.includes(product.id)
                  );
                  
                  return otherProducts.map((product, index) => (
                    <button
                      key={`other-${product.id}`}
                      className="btn btn-secondary btn-outline btn-lg w-full text-left"
                      onClick={() => handleProductSelectFromMaster(product)}
                    >
                      <div>
                        <div className="font-bold">{product.name}</div>
                        <div className="text-sm opacity-70">{product.item_ref}</div>
                        <div className="text-xs opacity-50">Not expected in this location</div>
                      </div>
                    </button>
                  ));
                })()}
              </div>
              
              <button
                className="btn btn-ghost btn-lg w-full mt-6"
                onClick={() => setMode('product')}
              >
                Back to scanning
              </button>
            </div>
          </div>
        )}

        {/* Quantity Entry Mode */}
        {mode === 'quantity' && currentItem && (
          <div className="text-center">
            <h1 className="text-lg font-bold mb-2">Enter Quantity</h1>
            
            <div className="text-lg font-bold mb-3">{currentItem.products.name}</div>
            
            <input
              type="number"
              placeholder="How many did you count?"
              className="input input-bordered w-full text-center text-2xl mb-3"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              autoFocus
            />
            
            <button
              className="btn btn-success w-full"
              onClick={handleQuantitySubmit}
              disabled={!quantity}
            >
              <Check className="w-4 h-4 mr-2" />
              Submit Count
            </button>
          </div>
        )}

        {/* Location Review Mode */}
        {mode === 'location_review' && (
          <div className="text-center">
            <h1 className="text-lg font-bold mb-3">Review & Confirm</h1>
              

              
              {/* Check if this is an empty location */}
              {currentLocation.items.length === 1 && currentLocation.items[0].products.item_ref === 'EMPTY_LOCATION' ? (
                <div className="mb-8">
                  <div className="alert alert-info mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>This location was expected to be empty</span>
                  </div>
                  
                  <div className="text-lg mb-6">
                    Status: {currentLocation.items[0].status === 'completed' ? 'Confirmed Empty' : 'Needs Confirmation'}
                  </div>
                  
                  <p className="text-lg mb-8">
                    Can you confirm this location is empty with no products?
                  </p>
                  
                  <div className="space-y-4">
                    <button
                      className="btn btn-primary btn-lg w-full"
                      onClick={handleConfirmLocation}
                    >
                      <ArrowRight className="w-6 h-6 mr-2" />
                      Yes, location is empty
                    </button>
                    
                    <button
                      className="btn btn-outline btn-lg w-full"
                      onClick={() => setMode('product')}
                    >
                      No, found products here
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-8">
                  {/* List of counted items */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Items Counted:</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {currentLocation.items
                        .filter(item => item.status === 'completed')
                        .map((item) => (
                          <div key={item.id} className="flex justify-between items-center p-3 bg-base-200 rounded">
                            <div className="text-left">
                              <div className="font-medium">{item.products.name}</div>
                              <div className="text-sm opacity-70">{item.products.item_ref}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold">{item.actual_quantity}</div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  <p className="text-lg mb-8">
                    Have you counted all products in this location?
                  </p>
                  
                  <div className="space-y-4">
                    <button
                      className="btn btn-primary btn-lg w-full"
                      onClick={handleConfirmLocation}
                    >
                      <ArrowRight className="w-6 h-6 mr-2" />
                      Yes, continue to next location
                    </button>
                    
                    <button
                      className="btn btn-outline btn-lg w-full"
                      onClick={() => setMode('product')}
                    >
                      No, scan more products
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Confirm Variance Mode */}
        {mode === 'confirm_variance' && currentItem && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Double Check</h1>
              
              <div className="text-xl font-bold mb-4">{currentItem.products.name}</div>
              
              <div className="bg-warning/20 p-4 rounded mb-8">
                <div className="text-sm text-base-content/70">You counted</div>
                <div className="text-2xl font-bold">{quantity}</div>
              </div>
              
              <p className="text-lg mb-8">
                This count seems unusual. Please double-check your count.
              </p>
              
              <div className="space-y-4">
                <button
                  className="btn btn-warning btn-lg w-full"
                  onClick={() => setMode('quantity')}
                >
                  Recount
                </button>
                
                <button
                  className="btn btn-success btn-lg w-full"
                  onClick={submitQuantity}
                >
                  <Check className="w-6 h-6 mr-2" />
                  Count is correct
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Missing Items Mode */}
        {mode === 'confirm_missing_items' && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Missing Items</h1>
              
              <div className="alert alert-warning mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L3.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>The following items haven't been counted yet.</span>
              </div>
              
              <p className="text-lg mb-6">
                Can you confirm there are <strong>none</strong> of these items in this location?
              </p>
              
              {/* List of missing items */}
              <div className="mb-8">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {currentLocation.items
                    .filter(item => item.status !== 'completed')
                    .map((item) => (
                      <div key={item.id} className="p-3 bg-base-200 rounded">
                        <div className="text-center">
                          <div className="font-medium">{item.products.name}</div>
                          <div className="text-sm opacity-70">{item.products.item_ref}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              
              <div className="space-y-4">
                <button
                  className="btn btn-error btn-lg w-full text-sm"
                  onClick={handleConfirmMissingItems}
                >
                  <Check className="w-5 h-5 mr-2" />
                  Confirm - Items NOT in location
                </button>
                
                <button
                  className="btn btn-outline btn-lg w-full"
                  onClick={() => setMode('product')}
                >
                  Go back to add missing items
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Session Complete Mode */}
        {mode === 'session_complete' && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-4xl font-bold mb-6">Complete!</h1>
              
              <p className="text-xl mb-4">
                All locations have been counted successfully!
              </p>
              
              {session?.status !== 'completed' && (
                <div className="alert alert-info mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>Review and edit locations if needed, then close the count when finished.</span>
                </div>
              )}
              
              {/* Location Summary with Edit Buttons */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Edit Previous Locations</h2>
                <div className="grid grid-cols-1 gap-3">
                  {locations
                    .map((location, originalIndex) => ({ ...location, originalIndex }))
                    .sort((a, b) => a.location.localeCompare(b.location))
                    .map((location) => (
                    <div key={location.location} className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                      <div className="text-left">
                        <div className="font-bold">{location.location}</div>
                        <div className="text-sm text-base-content/70">
                          {location.items.filter(item => item.status === 'completed').length} / {location.items.length} items counted
                        </div>
                      </div>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleEditLocation(location.originalIndex)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-4">
                {session?.status !== 'completed' && (
                  <button
                    className="btn btn-success btn-lg w-full"
                    onClick={handleCloseSession}
                  >
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Close Count
                  </button>
                )}
                
                <button
                  className="btn btn-primary btn-lg w-full"
                  onClick={() => navigate('/scanner')}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Location Mode */}
        {mode === 'edit_location' && editingLocationIndex !== null && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Edit Location</h1>
                <div className="badge badge-outline badge-lg">
                  <MapPin className="w-4 h-4 mr-2" />
                  {locations[editingLocationIndex].location}
                </div>
              </div>
              
              <div className="overflow-x-auto mb-6">
                <table className="table table-compact w-full">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Actual Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations[editingLocationIndex].items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div>
                            <div className="font-medium text-sm">{item.products.name}</div>
                            <div className="text-xs text-base-content/70">{item.products.item_ref}</div>
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="input input-bordered input-sm w-20 text-center"
                            value={item.actual_quantity || ''}
                            onChange={(e) => {
                              const newQuantity = parseInt(e.target.value) || 0;
                              handleUpdateCount(item.id, newQuantity);
                            }}
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="space-y-4">
                <button
                  className="btn btn-outline btn-lg w-full"
                  onClick={() => setMode('edit_add_item')}
                >
                  <Package className="w-6 h-6 mr-2" />
                  Add Item
                </button>
                
                <button
                  className="btn btn-primary btn-lg w-full"
                  onClick={() => setMode('session_complete')}
                >
                  Done Editing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Add Item Mode */}
        {mode === 'edit_add_item' && editingLocationIndex !== null && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Add Product</h1>
              
              {scanError && (
                <div className="alert alert-error mb-4">
                  <span>{scanError}</span>
                </div>
              )}
              
              <input
                type="text"
                placeholder="Scan or type barcode here"
                className={`input input-bordered input-lg w-full text-center text-2xl mb-8 ${scanError ? 'input-error' : ''}`}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleEditBarcodeSubmit();
                  }
                }}
                autoFocus
              />
              
              <div className="space-y-4">
                <button
                  className="btn btn-success btn-lg w-full"
                  onClick={handleEditBarcodeSubmit}
                  disabled={!barcode.trim()}
                >
                  <Package className="w-6 h-6 mr-2" />
                  Find Product
                </button>
                
                <button
                  className="btn btn-outline btn-lg w-full"
                  onClick={() => setMode('edit_no_barcode')}
                >
                  <FileText className="w-6 h-6 mr-2" />
                  No Barcode? Select from List
                </button>
                
                <button
                  className="btn btn-ghost btn-lg w-full"
                  onClick={() => setMode('edit_location')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit No Barcode Mode */}
        {mode === 'edit_no_barcode' && editingLocationIndex !== null && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Select Product</h1>
              
              <p className="text-lg mb-6">
                Select any product to add to this location:
              </p>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {/* Products expected in current location first */}
                <div className="text-sm font-semibold text-left mb-2">Expected in this location:</div>
                {locations[editingLocationIndex].items
                  .filter(item => item.status !== 'completed')
                  .map((item, index) => (
                    <button
                      key={`current-${item.id}`}
                      className="btn btn-outline btn-lg w-full text-left"
                      onClick={() => handleEditProductSelect(index, true)}
                    >
                      <div>
                        <div className="font-bold">{item.products.name}</div>
                        <div className="text-sm opacity-70">{item.products.item_ref}</div>
                      </div>
                    </button>
                  ))}
                
                {/* All other unique products */}
                <div className="text-sm font-semibold text-left mb-2 mt-4">All other products:</div>
                {(() => {
                  const currentLocationProductIds = locations[editingLocationIndex].items.map(item => item.product_id);
                  const otherProducts = allProducts.filter(product => 
                    !currentLocationProductIds.includes(product.id)
                  );
                  
                  return otherProducts.map((product, index) => (
                    <button
                      key={`other-${product.id}`}
                      className="btn btn-secondary btn-outline btn-lg w-full text-left"
                      onClick={() => handleEditProductSelectFromMaster(product)}
                    >
                      <div>
                        <div className="font-bold">{product.name}</div>
                        <div className="text-sm opacity-70">{product.item_ref}</div>
                        <div className="text-xs opacity-50">Not expected in this location</div>
                      </div>
                    </button>
                  ));
                })()}
              </div>
              
              <button
                className="btn btn-ghost btn-lg w-full mt-6"
                onClick={() => setMode('edit_add_item')}
              >
                Back to scanning
              </button>
            </div>
          </div>
        )}

        {/* Edit Quantity Mode */}
        {mode === 'edit_quantity' && editingLocationIndex !== null && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body text-center">
              <h1 className="text-3xl font-bold mb-6">Enter Quantity</h1>
              
              <div className="text-2xl font-bold mb-8">
                {locations[editingLocationIndex].items[currentItemIndex]?.products.name}
              </div>
              
              <input
                type="number"
                placeholder="How many did you count?"
                className="input input-bordered input-lg w-full text-center text-3xl mb-8"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                autoFocus
              />
              
              <div className="space-y-4">
                <button
                  className="btn btn-success btn-lg w-full"
                  onClick={handleEditQuantitySubmit}
                  disabled={!quantity}
                >
                  <Check className="w-6 h-6 mr-2" />
                  Add to Location
                </button>
                
                <button
                  className="btn btn-ghost btn-lg w-full"
                  onClick={() => setMode('edit_location')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>

      {/* Camera Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showBarcodeScanner}
        onScan={handleCameraScan}
        onClose={() => setShowBarcodeScanner(false)}
      />
    </div>
  );
};

export default CountSession; 